import * as yup from 'yup';
import { format, isValid, parseISO, subYears } from '../date';
import { t } from '../i18n';
import { getMinimumAgeRequirementByStateIso } from '../types';
import { isValidSIN } from './_util';
export * from 'yup';
/* =================== CUSTOM METHODS =================== */
yup.addMethod(yup.string, 'emptyToNull', function () {
    return this.transform(function (value) {
        return value?.trim() || null;
    });
});
yup.addMethod(yup.array, 'unique', function (mapper, message) {
    const m = mapper ?? ((a) => a);
    return this.test('unique', message ?? (() => t('array.unique', { ns: 'validation' })), function (list) {
        return !list?.length || list.length === new Set(list.map(m)).size;
    });
});
yup.addMethod(yup.string, 'validAgeRequirement', function (ref, message) {
    return this.test('test-validAgeRequirement', message ?? (() => t('custom.ageRequirement', { ns: 'validation' })), function (value) {
        const stateIso = this.resolve(ref);
        const legalAge = getMinimumAgeRequirementByStateIso(stateIso);
        return !value || new Date(value) <= subYears(new Date(), legalAge);
    });
});
yup.addMethod(yup.string, 'validDate', function (message) {
    return this.test('test-validDate', message ?? (() => t('custom.date', { ns: 'validation' })), function (value) {
        return !value || isValid(parseISO(value));
    });
});
yup.addMethod(yup.string, 'dateOnly', function (message) {
    return this.test('test-validDate', message ?? (() => t('custom.date', { ns: 'validation' })), function (value) {
        return !value || isValid(parseISO(value));
    }).transform(function (value) {
        if (!value)
            return null;
        return format(parseISO(value), 'yyyy-MM-dd');
    });
});
yup.addMethod(yup.string, 'validPhoneExtension', function (message) {
    return this.test('test-validPhoneExtension', message ?? (() => t('custom.extension', { ns: 'validation' })), function (value) {
        return !value || new RegExp('^[0-9]{1,10}$').test(value);
    });
});
yup.addMethod(yup.string, 'validCanadianPhone', function (message) {
    return this.test('test-validCanadianPhone', message ?? (() => t('custom.phone', { ns: 'validation' })), function (value) {
        return !value || new RegExp('^([1][-]?)?[2-9][0-9]{2}[-]?[0-9]{3}[-]?[0-9]{4}$').test(value);
    });
});
yup.addMethod(yup.string, 'validCanadianPostalCode', function (message) {
    return this.test('test-PostalCode-type', message ?? (() => t('custom.postalCode', { ns: 'validation' })), function (value) {
        return (!value ||
            new RegExp('^[ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ] ?[0-9][ABCEGHJKLMNPRSTVWXYZ][0-9]$').test(value.toUpperCase()));
    });
});
yup.addMethod(yup.string, 'validSIN', function (message) {
    return this.test('test-validSIN', message ?? (() => t('custom.sin', { ns: 'validation' })), function (value) {
        return !value || isValidSIN(value);
    });
});
yup.addMethod(yup.string, 'validCharacters', function (message) {
    return this.matches(/^[^0-9_!¡?÷?¿/+=@#$%ˆ&*(){}|~<>;":[]*$/, message ?? (() => t('custom.illegalCharacters', { ns: 'validation' })));
});
yup.addMethod(yup.number, 'validPercent', function (message) {
    return this.test('test-validPercent', message ?? (() => t('custom.percent', { ns: 'validation' })), function (value) {
        if (!value)
            return true;
        const dot = value.toString().replace(',', '.').indexOf('.');
        const decimal = value.toString().substring(dot);
        if (dot === -1)
            return true;
        if (decimal.length <= 3)
            return true;
        return false;
    });
});
/* =================== CUSTOM SCHEMAS =================== */
export const double = () => yup
    .number()
    .transform((_, val) => {
    if (val === '' || val === null || val === undefined) {
        return null;
    }
    const x = Number(String(val).replace(',', '.'));
    const n = Math.round(x * 100) / 100;
    if (Number.isFinite(n))
        return n;
    return NaN;
})
    .default(null)
    .nullable()
    .typeError(() => t('mixed.default', { ns: 'validation' }));
export const integer = () => yup
    .number()
    .transform((_, val) => {
    if (val === '' || val === null || val === undefined)
        return null;
    if (typeof val === 'string' && val.trim() === '')
        return NaN;
    const n = Number(val);
    if (Number.isInteger(n))
        return n;
    return NaN;
})
    .default(null)
    .nullable()
    .typeError(() => t('mixed.default', { ns: 'validation' }));
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const numberEnum = (oneOf) => {
    let ret = yup
        .mixed()
        .transform((_, val) => {
        if (val === '' || val === null || val === undefined)
            return null;
        if (typeof val === 'string' && val.trim() === '')
            return NaN;
        const n = Number(val);
        if (Number.isInteger(n))
            return n;
        return NaN;
    })
        .nullable();
    if (oneOf)
        ret = ret.oneOf(oneOf);
    return ret.typeError(() => t('mixed.default', { ns: 'validation' }));
};
/* =================== CUSTOM ERROR MESSAGES =================== */
yup.setLocale({
    mixed: {
        default: () => t('mixed.default', { ns: 'validation' }),
        required: () => t('mixed.required', { ns: 'validation' }),
        defined: () => t('mixed.defined', { ns: 'validation' }),
        notNull: () => t('mixed.notNull', { ns: 'validation' }),
        oneOf: () => t('mixed.oneOf', { ns: 'validation' }),
        notOneOf: () => t('mixed.notOneOf', { ns: 'validation' }),
    },
    string: {
        length: ({ length }) => t('string.length', { count: length, ns: 'validation' }),
        email: () => t('string.email', { ns: 'validation' }),
        min: ({ min }) => t('string.min', { count: min, ns: 'validation' }),
        max: ({ max }) => t('string.max', { count: max, ns: 'validation' }),
        matches: () => t('string.matches', { ns: 'validation' }),
        url: () => t('string.url', { ns: 'validation' }),
        uuid: () => t('string.uuid', { ns: 'validation' }),
        trim: () => t('string.trim', { ns: 'validation' }),
        lowercase: () => t('string.lowercase', { ns: 'validation' }),
        uppercase: () => t('string.uppercase', { ns: 'validation' }),
    },
    number: {
        min: ({ min }) => t('number.min', { min, ns: 'validation' }),
        max: ({ max }) => t('number.max', { max, ns: 'validation' }),
        lessThan: ({ less }) => t('number.lessThan', { less, ns: 'validation' }),
        moreThan: ({ more }) => t('number.moreThan', { more, ns: 'validation' }),
        positive: () => t('number.positive', { ns: 'validation' }),
        negative: () => t('number.negative', { ns: 'validation' }),
        integer: () => t('number.integer', { ns: 'validation' }),
    },
    date: {
        min: ({ min }) => t('date.min', { min, ns: 'validation' }),
        max: ({ max }) => t('date.max', { max, ns: 'validation' }),
    },
    array: {
        min: ({ min }) => t('array.min', { count: min, ns: 'validation' }),
        max: ({ max }) => t('array.max', { count: max, ns: 'validation' }),
        length: ({ length }) => t('array.length', { count: length, ns: 'validation' }),
    },
    object: {
        noUnknown: () => t('object.noUnknown', { ns: 'validation' }),
    },
    boolean: {
        isValue: ({ value }) => value ? t('boolean.isTrue', { ns: 'validation' }) : t('boolean.isFalse', { ns: 'validation' }),
    },
});
